import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { type Category } from "@/hooks/useCategories";

interface BrowseFiltersProps {
  variant: 'desktop' | 'mobile';
  categories: Category[];
  isEducation: boolean;
  priceRange: number[];
  selectedCategories: string[];
  circleSelectOnly: boolean;
  selectedBestFor: string[];
  selectedIntegrations: string[];
  selectedSkillLevel: string;
  selectedDurations: string[];
  selectedInclusions: string[];
  showAllIntegrations: boolean;
  integrationOptions: string[];
  onPriceRangeChange: (value: number[]) => void;
  onCategoryToggle: (slug: string) => void;
  onCircleSelectChange: (checked: boolean) => void;
  onBestForToggle: (id: string) => void;
  onIntegrationToggle: (name: string) => void;
  onSkillLevelChange: (level: string) => void;
  onDurationToggle: (duration: string) => void;
  onInclusionToggle: (inclusion: string) => void;
  onShowAllIntegrationsToggle: () => void;
}

export function BrowseFilters({
  variant,
  categories,
  isEducation,
  priceRange,
  selectedCategories,
  circleSelectOnly,
  selectedBestFor,
  selectedIntegrations,
  selectedSkillLevel,
  selectedDurations,
  selectedInclusions,
  showAllIntegrations,
  integrationOptions,
  onPriceRangeChange,
  onCategoryToggle,
  onCircleSelectChange,
  onBestForToggle,
  onIntegrationToggle,
  onSkillLevelChange,
  onDurationToggle,
  onInclusionToggle,
  onShowAllIntegrationsToggle,
}: BrowseFiltersProps) {
  const idPrefix = variant === 'mobile' ? 'mobile-' : 'desktop-';

  return (
    <div className="space-y-6 pb-6">
      {/* Categories */}
      <div>
        <h3 className="font-semibold mb-3">Shop by category:</h3>
        <div className="space-y-2">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox 
                id={`${idPrefix}${category.slug}`}
                checked={selectedCategories.includes(category.slug)}
                onCheckedChange={() => onCategoryToggle(category.slug)}
              />
              <Label htmlFor={`${idPrefix}${category.slug}`} className="text-sm cursor-pointer">
                {category.name} {category.product_count && <span className="text-muted-foreground">({category.product_count})</span>}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {isEducation ? (
        <>
          {/* Skill Level */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-3">Skill Level</h3>
            <RadioGroup value={selectedSkillLevel} onValueChange={onSkillLevelChange}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="" id={`${idPrefix}skill-all`} />
                <Label htmlFor={`${idPrefix}skill-all`} className="text-sm cursor-pointer">All Levels</Label>
              </div>
              {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                <div key={level} className="flex items-center space-x-2">
                  <RadioGroupItem value={level} id={`${idPrefix}skill-${level.toLowerCase()}`} />
                  <Label htmlFor={`${idPrefix}skill-${level.toLowerCase()}`} className="text-sm cursor-pointer">
                    {level}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Course Duration */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-3">Course Duration</h3>
            <div className="space-y-2">
              {[
                { label: 'Under 2 hours', value: '<2hr' },
                { label: '2-5 hours', value: '2-5hr' },
                { label: '5-10 hours', value: '5-10hr' },
                { label: '10+ hours', value: '10+hr' }
              ].map((duration) => (
                <div key={duration.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${idPrefix}duration-${duration.value}`}
                    checked={selectedDurations.includes(duration.value)}
                    onCheckedChange={() => onDurationToggle(duration.value)}
                  />
                  <Label htmlFor={`${idPrefix}duration-${duration.value}`} className="text-sm cursor-pointer">
                    {duration.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* What's Included */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-3">What's Included</h3>
            <div className="space-y-2">
              {[
                { label: 'Certificate', value: 'certificate' },
                { label: 'Preview Lesson', value: 'preview' },
                { label: 'Lifetime Access', value: 'lifetime' }
              ].map((inclusion) => (
                <div key={inclusion.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${idPrefix}inclusion-${inclusion.value}`}
                    checked={selectedInclusions.includes(inclusion.value)}
                    onCheckedChange={() => onInclusionToggle(inclusion.value)}
                  />
                  <Label htmlFor={`${idPrefix}inclusion-${inclusion.value}`} className="text-sm cursor-pointer">
                    {inclusion.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Circle Select */}
          <div className="pt-4 border-t">
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox 
                id={`${idPrefix}select`}
                checked={circleSelectOnly}
                onCheckedChange={(checked) => onCircleSelectChange(checked as boolean)}
              />
              <Label htmlFor={`${idPrefix}select`} className="font-semibold cursor-pointer flex items-center gap-1">
                <span className="font-handelson font-extralight bg-deal-badge text-deal-badge-foreground px-3 py-px rounded-md text-xl leading-none">Circle</span>
                SELECT
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-6">Premium tools vetted by our team</p>
          </div>

          {/* Best for */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-3">Best for</h3>
            <div className="space-y-2">
              {[
                { id: 'new-agents', label: 'New Agents' },
                { id: 'growing-agents', label: 'Growing Agents' },
                { id: 'top-producers', label: 'Top Producers' },
                { id: 'team-leaders-brokers', label: 'Team Leaders & Brokers' }
              ].map((item) => (
                <div key={item.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`${idPrefix}${item.id}`}
                    checked={selectedBestFor.includes(item.id)}
                    onCheckedChange={() => onBestForToggle(item.id)}
                  />
                  <Label htmlFor={`${idPrefix}${item.id}`} className="text-sm cursor-pointer">{item.label}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Integrations */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-3">Integrations</h3>
            <div className="space-y-2">
              {(showAllIntegrations ? integrationOptions : integrationOptions.slice(0, 7)).map(option => (
                <div key={option} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${idPrefix}integration-${option}`}
                    checked={selectedIntegrations.includes(option)}
                    onCheckedChange={() => onIntegrationToggle(option)}
                  />
                  <Label htmlFor={`${idPrefix}integration-${option}`} className="text-sm cursor-pointer">{option}</Label>
                </div>
              ))}
            </div>
            {integrationOptions.length > 7 && (
              <Button variant="ghost" size="sm" onClick={onShowAllIntegrationsToggle} className="mt-2 w-full text-xs">
                {showAllIntegrations ? 'Show Less' : `Show More (${integrationOptions.length - 7})`}
              </Button>
            )}
          </div>
        </>
      )}

      {/* Price Range */}
      <div className="pt-4 border-t">
        <h3 className="font-semibold mb-4">Price range</h3>
        <Slider value={priceRange} onValueChange={onPriceRangeChange} max={15000} step={100} minStepsBetweenThumbs={1} className="mb-2" />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>${priceRange[0]}</span>
          <span>${priceRange[1]}</span>
        </div>
      </div>
    </div>
  );
}
